import { useState } from 'react';
import { SlidersHorizontal, ArrowUpDown, ArrowDown, RotateCcw, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Hero } from '../components/Hero';
import { FeaturedPlans } from '../components/FeaturedPlans';
import { FilterSidebar } from '../components/FilterSidebar';
import { PlanCard } from '../components/PlanCard';
import { ComparisonTable } from '../components/ComparisonTable';
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { usePlans } from '../hooks/usePlans';
import { SortOption } from '../types';
import { Wifi } from 'lucide-react';
import { HomeSeo } from '../components/HomeSeo';
import { useIsMobile } from '../hooks/use-mobile';
import { NewsletterSignup } from '../components/NewsletterSignup';

const sortOptions = [
  { value: 'relevance', label: 'Relevans' },
  { value: 'priceAsc', label: 'Pris: Lav til høj' },
  { value: 'priceDesc', label: 'Pris: Høj til lav' },
  { value: 'dataAsc', label: 'Data: Lav til høj' },
  { value: 'dataDesc', label: 'Data: Høj til lav' },
  { value: 'ratingDesc', label: 'Højeste bedømmelse' },
  { value: 'popularityDesc', label: 'Mest populære' }
];

const Index = () => {
  const {
    filteredPlans,
    filters,
    setFilters,
    sortBy,
    setSortBy,
    searchQuery,
    setSearchQuery,
    selectedPlans,
    plansToCompare,
    togglePlanSelection,
    resetFilters,
    sortDataAllowances,
    allPlans
  } = usePlans();
  
  const [showComparisonView, setShowComparisonView] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  const isMobile = useIsMobile();
  
  const hasActiveFilters = 
    filters.providers.length > 0 || 
    filters.dataAllowances.length > 0 || 
    filters.contractLengths.length > 0 || 
    filters.roaming.length > 0 || 
    filters.has5G !== undefined || 
    filters.minPrice !== undefined && filters.minPrice > 0 || 
    filters.maxPrice !== undefined && filters.maxPrice < 500;
  
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (query) {
      const element = document.getElementById('results');
      setTimeout(() => {
        element?.scrollIntoView({
          behavior: 'smooth'
        });
      }, 100);
    }
  };
  
  const handleViewAll = () => {
    const element = document.getElementById('results');
    element?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  
  const handleSortChange = (value: string) => {
    setSortBy(value as SortOption);
  };
  
  const handleClearSelection = () => {
    setShowComparisonView(false);
  };
  
  const toggleFilters = () => {
    setShowFilters(prev => !prev);
  };
  
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main>
        <Hero onSearch={handleSearch} />
        
        <FeaturedPlans onViewAll={handleViewAll} />
        
        <section id="results" className="py-20">
          <div className="container mx-auto md:px-6 px-0">
            <div className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-3">
                {searchQuery ? `Søgeresultater for "${searchQuery}"` : 'Alle Mobilabonnementer'}
              </h2>
              <p className="text-muted-foreground">
                {filteredPlans.length} abonnementer fundet
              </p>
            </div>
            
            {/* Mobile filter and sort buttons */}
            <div className="lg:hidden flex space-x-3 mb-6">
              <Button 
                variant="outline" 
                className="flex-1" 
                onClick={toggleFilters}
                aria-label="Vis filtre"
                aria-expanded={showFilters}
              >
                <SlidersHorizontal className="h-4 w-4 mr-2" />
                Filtre
              </Button>
              
              <Select 
                defaultValue={sortBy} 
                onValueChange={handleSortChange}
                aria-label="Sorter abonnementer"
              >
                <SelectTrigger className="flex-1" aria-label="Vælg sortering">
                  <ArrowUpDown className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Sorter" />
                </SelectTrigger>
                <SelectContent>
                  {sortOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Mobile filters */}
            <div className={`lg:hidden mb-6 ${showFilters ? 'block' : 'hidden'}`}>
              <FilterSidebar 
                filters={filters} 
                onFiltersChange={setFilters} 
                onReset={resetFilters} 
                allPlans={allPlans} 
                sortDataAllowances={sortDataAllowances} 
              />
            </div>
            
            <div className="container mx-auto">
              {/* Desktop horizontal filters - now full width */}
              <div className="hidden lg:block mb-6">
                <FilterSidebar 
                  filters={filters} 
                  onFiltersChange={setFilters} 
                  onReset={resetFilters} 
                  allPlans={allPlans} 
                  sortDataAllowances={sortDataAllowances}
                  className="shadow-sm"
                />
              </div>
              
              {/* Sort options (desktop) */}
              <div className="hidden lg:flex justify-end items-center mb-6">
                <div className="flex items-center space-x-2">
                  <span className="text-sm">Sorter efter:</span>
                  <Select defaultValue={sortBy} onValueChange={handleSortChange}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Sorter efter" />
                    </SelectTrigger>
                    <SelectContent>
                      {sortOptions.map(option => <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Selected plans bar */}
              {selectedPlans.length > 0 && <div className="bg-secondary/50 rounded-lg mb-6 p-4 flex justify-between items-center">
                  <div>
                    <span className="font-medium">{selectedPlans.length} abonnement{selectedPlans.length !== 1 ? 'er' : ''} valgt</span>
                    <p className="text-sm text-muted-foreground">Vælg op til 3 abonnementer for at sammenligne</p>
                  </div>
                  <Button 
                    onClick={() => setShowComparisonView(!showComparisonView)} 
                    className="bg-primary/90 hover:bg-primary"
                    aria-label={showComparisonView ? "Skjul sammenligning af abonnementer" : "Vis sammenligning af abonnementer"}
                    aria-expanded={showComparisonView}
                  >
                    {showComparisonView ? 'Skjul sammenligning' : 'Sammenlign abonnementer'}
                  </Button>
                </div>}
              
              {showComparisonView && selectedPlans.length > 0 && <ComparisonTable plans={plansToCompare} onClearSelection={handleClearSelection} className="mb-8 animate-fade-in" />}
              
              {/* Plans grid or empty state */}
              {filteredPlans.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {filteredPlans.map((plan, index) => <PlanCard key={plan.id} plan={plan} isSelected={selectedPlans.includes(plan.id)} onSelect={togglePlanSelection} className={`animate-fade-up animate-in-${index % 3 + 1}`} />)}

                  {(searchQuery || hasActiveFilters) && <button 
                    onClick={() => {
                      setSearchQuery('');
                      resetFilters();
                    }} 
                    className="relative h-full glass-card overflow-hidden flex flex-col items-center justify-center p-6 text-center cursor-pointer group hover:ring-2 hover:ring-primary transition-all animate-fade-up"
                    aria-label="Vis alle abonnementer ved at nulstille søgning og filtre"
                  >
                    <div className="mb-4 p-4 rounded-full bg-secondary/50 group-hover:bg-primary/10 transition-colors">
                      <RotateCcw className="h-8 w-8 text-muted-foreground group-hover:text-primary transition-colors" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">Vis alle abonnementer</h3>
                    <p className="text-sm text-muted-foreground">
                      Nulstil søgning og filtre for at se alle tilgængelige abonnementer
                    </p>
                  </button>}
                </div> : <div className="text-center py-16 px-4">
                  <div className="mb-4">
                    <div className="mx-auto w-16 h-16 rounded-full bg-secondary flex items-center justify-center">
                      <ArrowDown className="h-8 w-8 text-muted-foreground" />
                    </div>
                  </div>
                  <h3 className="text-xl font-medium mb-2">Ingen abonnementer passer dine kriterier</h3>
                  <p className="text-muted-foreground mb-6">Prøv at justere dine filtre eller søgeord</p>
                  <Button onClick={resetFilters} aria-label="Nulstil alle filtre">Nulstil Filtre</Button>
                </div>}
            </div>
          </div>
        </section>
        
        {/* Newsletter section */}
        <section className="py-20 bg-gradient-to-b from-transparent to-secondary/30">
          <div className="container mx-auto px-4 md:px-6 max-w-4xl">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-3">Hold dig opdateret med de bedste tilbud</h2>
              <p className="text-muted-foreground">
                Tilmeld dig vores nyhedsbrev for at modtage opdateringer om de nyeste abonnementer og eksklusive tilbud
              </p>
            </div>
            
            <NewsletterSignup />
          </div>
        </section>
        
        <HomeSeo />
      </main>
      
      {/* Footer */}
      <footer className="bg-secondary/40 py-12">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-6 md:mb-0">
              <div className="flex items-center">
                <Wifi className="h-6 w-6 text-primary mr-2" />
                <span className="text-xl font-bold">
                  Abo<span className="text-primary">n</span><span className="text-primary">emt</span>
                </span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Vi hjælper dig med at finde det perfekte mobilabonnement
              </p>
            </div>
            
            <div className="flex space-x-8 text-sm text-muted-foreground">
              <Link to="/about" className="hover:text-foreground transition-colors">Om os</Link>
              <Link to="/contact" className="hover:text-foreground transition-colors">Kontakt</Link>
              <Link to="/privacy" className="hover:text-foreground transition-colors">Privatlivspolitik</Link>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-border text-center text-sm text-muted-foreground">
            <p>© {new Date().getFullYear()} Abonemt. Alle rettigheder forbeholdes.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
